import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { FiMenu, FiX, FiUser, FiLogOut, FiUpload } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

const NavContainer = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid var(--neutral-gray-200);
  z-index: var(--z-fixed);
  height: ${props => props.isMobile ? '70px' : '80px'};
`;

const NavContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--space-md);
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Logo = styled(Link)`
  font-family: var(--font-family-display);
  font-size: ${props => props.isMobile ? '1.25rem' : '1.5rem'};
  font-weight: 700;
  color: var(--primary-green);
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  
  &:hover {
    color: var(--primary-green-dark);
  }
`;

const LogoIcon = styled.div`
  width: ${props => props.isMobile ? '32px' : '40px'};
  height: ${props => props.isMobile ? '32px' : '40px'};
  background: linear-gradient(135deg, var(--primary-green), var(--primary-green-light));
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: ${props => props.isMobile ? '14px' : '16px'};
`;

const DesktopNav = styled.div`
  display: none;
  
  @media (min-width: 768px) {
    display: flex;
    align-items: center;
    gap: var(--space-lg);
  }
`;

const NavLinks = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-lg);
`;

const NavLink = styled(Link)`
  color: var(--neutral-gray-700);
  font-weight: 500;
  text-decoration: none;
  padding: var(--space-sm) var(--space-md);
  border-radius: var(--radius-md);
  transition: var(--transition-base);
  position: relative;
  
  &:hover {
    color: var(--primary-green);
    background-color: var(--neutral-gray-50);
  }
  
  ${props => props.active && `
    color: var(--primary-green);
    background-color: var(--neutral-gray-100);
  `}
`;

const UserMenu = styled.div`
  position: relative;
`;

const UserButton = styled.button`
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  padding: var(--space-sm) var(--space-md);
  border-radius: var(--radius-md);
  background: none;
  border: none;
  color: var(--neutral-gray-700);
  font-weight: 500;
  cursor: pointer;
  transition: var(--transition-base);
  
  &:hover {
    background-color: var(--neutral-gray-100);
  }
`;

const UserDropdown = styled(motion.div)`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: var(--space-sm);
  background: white;
  border: 1px solid var(--neutral-gray-200);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  padding: var(--space-sm);
  min-width: 200px;
  z-index: var(--z-dropdown);
`;

const DropdownItem = styled(Link)`
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  padding: var(--space-sm) var(--space-md);
  color: var(--neutral-gray-700);
  text-decoration: none;
  border-radius: var(--radius-md);
  transition: var(--transition-base);
  
  &:hover {
    background-color: var(--neutral-gray-50);
    color: var(--primary-green);
  }
`;

const DropdownButton = styled.button`
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  padding: var(--space-sm) var(--space-md);
  color: var(--neutral-gray-700);
  background: none;
  border: none;
  border-radius: var(--radius-md);
  transition: var(--transition-base);
  width: 100%;
  text-align: left;
  cursor: pointer;
  
  &:hover {
    background-color: var(--neutral-gray-50);
    color: var(--accent-orange);
  }
`;

const MobileMenuButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border: none;
  background: none;
  color: var(--neutral-gray-700);
  cursor: pointer;
  border-radius: var(--radius-md);
  transition: var(--transition-base);
  
  &:hover {
    background-color: var(--neutral-gray-100);
  }
  
  /* Show only on mobile */
  @media (max-width: 767px) {
    display: flex;
  }
  
  @media (min-width: 768px) {
    display: none;
  }
`;

const MobileMenu = styled(motion.div)`
  position: fixed;
  top: ${props => props.isMobile ? '70px' : '80px'};
  left: 0;
  right: 0;
  bottom: 0;
  background: white;
  backdrop-filter: blur(10px);
  z-index: 9999;
  padding: var(--space-lg);
  overflow-y: auto;
  border-top: 1px solid var(--neutral-gray-200);
  min-height: calc(100vh - ${props => props.isMobile ? '70px' : '80px'});
  height: auto;
  
  /* Ensure it's visible on mobile */
  @media (max-width: 767px) {
    display: flex;
    flex-direction: column;
  }
  
  @media (min-width: 768px) {
    display: none;
  }
`;

const MobileNavLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-md);
  width: 100%;
  flex: 1;
  min-height: 200px;
`;

const MobileNavLink = styled(Link)`
  color: var(--neutral-gray-700);
  font-weight: 500;
  font-size: 1.125rem;
  text-decoration: none;
  padding: var(--space-md) var(--space-lg);
  border-radius: var(--radius-lg);
  transition: var(--transition-base);
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 48px;
  
  &:hover {
    color: var(--primary-green);
    background-color: var(--neutral-gray-50);
  }
  
  ${props => props.active && `
    color: var(--primary-green);
    background-color: var(--neutral-gray-100);
  `}
`;

const MobileUserSection = styled.div`
  margin-top: var(--space-xl);
  padding-top: var(--space-xl);
  border-top: 1px solid var(--neutral-gray-200);
  display: flex;
  flex-direction: column;
  gap: var(--space-md);
`;

const Navigation = ({ user, onLogout, isMobile }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const location = useLocation();

  const navItems = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About' },
    { path: '/blog', label: 'Blog' },
    { path: '/parks', label: 'Parks' },
    { path: '/gallery', label: 'Gallery' },
    { path: '/upload', label: 'Upload' },
    { path: '/contact', label: 'Contact' }
  ];

  const isActivePath = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const handleLogout = () => {
    onLogout();
    setIsUserMenuOpen(false);
    setIsMobileMenuOpen(false);
  };

  return (
    <NavContainer isMobile={isMobile}>
      <NavContent>
        <Logo to="/" isMobile={isMobile}>
          <LogoIcon isMobile={isMobile}>RV</LogoIcon>
          {!isMobile && 'RV Better Together'}
          {isMobile && 'RVBT'}
        </Logo>

        {/* Desktop Navigation */}
        <DesktopNav>
          <NavLinks>
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                active={isActivePath(item.path)}
              >
                {item.label}
              </NavLink>
            ))}
          </NavLinks>

          {user ? (
            <UserMenu>
              <UserButton onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}>
                <FiUser size={18} />
                Hi, {user.name}
              </UserButton>
              
              <AnimatePresence>
                {isUserMenuOpen && (
                  <UserDropdown
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                  >
                    <DropdownItem to="/dashboard">
                      <FiUser size={16} />
                      Dashboard
                    </DropdownItem>
                    <DropdownItem to="/upload">
                      <FiUpload size={16} />
                      Upload Content
                    </DropdownItem>
                    <DropdownButton onClick={handleLogout}>
                      <FiLogOut size={16} />
                      Logout
                    </DropdownButton>
                  </UserDropdown>
                )}
              </AnimatePresence>
            </UserMenu>
          ) : (
            <NavLink to="/login">Login</NavLink>
          )}
        </DesktopNav>

        {/* Mobile Menu Button */}
        <MobileMenuButton onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
          {isMobileMenuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
        </MobileMenuButton>
      </NavContent>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <MobileMenu
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            isMobile={isMobile}
          >
            <MobileNavLinks>
              {navItems.map((item) => (
                <MobileNavLink
                  key={item.path}
                  to={item.path}
                  active={isActivePath(item.path)}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {item.label}
                </MobileNavLink>
              ))}
            </MobileNavLinks>

            <MobileUserSection>
              {user ? (
                <>
                  <MobileNavLink to="/dashboard" onClick={() => setIsMobileMenuOpen(false)}>
                    <FiUser size={20} style={{ marginRight: '8px' }} />
                    Dashboard
                  </MobileNavLink>
                  <MobileNavLink to="/upload" onClick={() => setIsMobileMenuOpen(false)}>
                    <FiUpload size={20} style={{ marginRight: '8px' }} />
                    Upload Content
                  </MobileNavLink>
                  <MobileNavLink as="button" onClick={handleLogout}>
                    <FiLogOut size={20} style={{ marginRight: '8px' }} />
                    Logout
                  </MobileNavLink>
                </>
              ) : (
                <MobileNavLink to="/login" onClick={() => setIsMobileMenuOpen(false)}>
                  Login
                </MobileNavLink>
              )}
            </MobileUserSection>
          </MobileMenu>
        )}
      </AnimatePresence>
    </NavContainer>
  );
};

export default Navigation;
